import { useEffect, useRef, useCallback, useState } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { usePaymentDetection } from '@/hooks/usePaymentDetection';
import { useBCHPrice } from '@/hooks/useBCHPrice';
import { useProPricing } from '@/hooks/useProPricing';
import { useWakeLock } from '@/hooks/useWakeLock';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { generateBCHUri, formatFiat } from '@/lib/bch';
import { PRO_ADDRESS, EARLY_LIMIT, REGULAR_PRICE_USD, FREE_PRODUCT_LIMIT, FREE_HISTORY_LIMIT } from '@/lib/pro';
import { cn } from '@/lib/utils';

const PRO_FEATURES = [
  { icon: '🛍️', label: 'Unlimited products', sub: `Free plan: ${FREE_PRODUCT_LIMIT} products` },
  { icon: '📷', label: 'Product photos', sub: 'Add images to menu items' },
  { icon: '🗂️', label: 'Product categories', sub: 'Organize your menu' },
  { icon: '🏪', label: 'Business logo', sub: 'Brand your checkout screen' },
  { icon: '📋', label: 'Full transaction history', sub: `Free plan: last ${FREE_HISTORY_LIMIT} only` },
  { icon: '📊', label: 'Analytics dashboard', sub: 'Revenue charts, peak hours & top products' },
  { icon: '⚡', label: 'Quick charge shortcuts', sub: 'One-tap buttons for frequent amounts' },
  { icon: '📥', label: 'CSV export', sub: 'Download your sales data' },
];

const PRO_PLUS_FEATURES = [
  { icon: '🔄', label: 'Multi-device sync', sub: 'Share settings & history across devices' },
  { icon: '🔔', label: 'Payment notifications', sub: 'Alerts & webhooks when you get paid' },
  { icon: '🧾', label: 'Advanced reporting', sub: 'PDF reports & accountant exports' },
  { icon: '🔢', label: 'Multiple addresses', sub: 'Rotate or assign addresses per product' },
];

function ProPlusTeaser() {
  return (
    <div className="rounded-2xl overflow-hidden border border-violet-200 dark:border-violet-800/50">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-500 to-violet-600 px-4 py-3.5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
          <span className="text-white font-black text-xs">PRO+</span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-white">Pro Plus — Coming Soon</p>
          <p className="text-[11px] text-violet-200">A separate paid upgrade for power users</p>
        </div>
        <span className="text-[10px] font-bold text-violet-200 bg-white/10 border border-white/20 px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0">
          Future
        </span>
      </div>

      {/* Feature list */}
      <div className="divide-y divide-violet-100 dark:divide-violet-900/40">
        {PRO_PLUS_FEATURES.map((f) => (
          <div key={f.label} className="flex items-center gap-3 px-4 py-3 bg-violet-50/50 dark:bg-violet-950/20">
            <span className="text-xl w-8 text-center shrink-0 opacity-50">{f.icon}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-500 dark:text-zinc-400">{f.label}</p>
              <p className="text-xs text-zinc-400 dark:text-zinc-500">{f.sub}</p>
            </div>
            <span className="text-[10px] font-bold text-violet-400 bg-violet-100 dark:bg-violet-900/40 px-2 py-0.5 rounded-full uppercase tracking-wide shrink-0">
              Soon
            </span>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="bg-violet-50 dark:bg-violet-950/30 px-4 py-3 flex items-center gap-2">
        <span className="text-violet-400 text-sm">💡</span>
        <p className="text-[11px] text-violet-500 dark:text-violet-400 leading-relaxed">
          Pro Plus will be a separate one-time upgrade — existing Pro users get a discount when it launches.
        </p>
      </div>
    </div>
  );
}

function playUnlockSound() {
  try {
    const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    const notes = [523.25, 659.25, 783.99, 1046.5, 1318.5];
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = freq;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.08);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + i * 0.08 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.08 + 0.4);
      osc.start(ctx.currentTime + i * 0.08);
      osc.stop(ctx.currentTime + i * 0.08 + 0.5);
    });
  } catch { /* silent fail */ }
}

export function UpgradeView() {
  const { setView, activatePro, isPro, settings } = usePOS();
  const { data: priceData } = useBCHPrice(settings.currency);
  const pricing = useProPricing();
  const [showQR, setShowQR] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const detectedRef = useRef(false);

  // Lock the BCH price at the moment the QR is shown — don't let live price
  // updates change expectedBCH mid-detection, which would reset knownTxids
  // and risk triggering on a pre-existing UTXO at the new price.
  const [lockedPriceBCH, setLockedPriceBCH] = useState<number | undefined>(undefined);

  const { priceBCH, priceUSD, isEarly, spotsLeft, isLoading: pricingLoading } = pricing;

  // priceBCH is undefined while loading — gate the QR/payment on it being ready
  const priceReady = priceBCH !== undefined && priceBCH > 0;

  const approxLocal = priceReady && priceData
    ? formatFiat(priceBCH * priceData.price, settings.currency)
    : `~$${priceUSD}`;

  const handleShowQR = () => {
    // Snapshot the price right now — detection uses this locked value
    setLockedPriceBCH(priceBCH);
    setShowQR(true);
  };

  const bchUri = lockedPriceBCH ? generateBCHUri(PRO_ADDRESS, lockedPriceBCH, 'BCHPAY.Cash Pro Unlock') : '';
  const qrSize = Math.min(window.innerWidth - 80, 260);

  const handleDetected = useCallback(({ txid }: { txid: string }) => {
    if (detectedRef.current) return;
    detectedRef.current = true;
    playUnlockSound();
    setCelebrating(true);
    activatePro(txid);
    setTimeout(() => setView('checkout'), 3500);
  }, [activatePro, setView]);

  const { status, reset } = usePaymentDetection(
    PRO_ADDRESS,
    lockedPriceBCH ?? 0,         // stable — won't change while QR is showing
    showQR && !celebrating && !!lockedPriceBCH,
    handleDetected
  );

  // Keep screen awake while the payment QR is showing
  useWakeLock(showQR && !celebrating);

  useEffect(() => {
    detectedRef.current = false;
    return () => reset();
  }, [reset]);

  // Already pro
  if (isPro && !celebrating) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <div className="flex flex-col items-center gap-3 pt-4 text-center">
          <div className="text-5xl">✅</div>
          <h2 className="text-2xl font-black text-zinc-900 dark:text-zinc-50">You're Pro!</h2>
          <p className="text-sm text-zinc-400">All current features are unlocked. More on the way.</p>
        </div>
        <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 divide-y divide-zinc-100 dark:divide-zinc-700 overflow-hidden">
          {PRO_FEATURES.map((f) => (
            <div key={f.label} className="flex items-center gap-3 px-4 py-3">
              <span className="text-xl w-8 text-center shrink-0">{f.icon}</span>
              <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 flex-1">{f.label}</p>
              <span className="text-[#0AC18E] font-bold text-lg shrink-0">✓</span>
            </div>
          ))}
        </div>
        <ProPlusTeaser />
        <button
          onClick={() => setView('checkout')}
          className="w-full py-4 rounded-2xl text-base font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30"
        >
          Back to Checkout
        </button>
      </div>
    );
  }

  // Celebration after unlock
  if (celebrating) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-6 px-4 text-center">
        <div className="relative">
          <div className="w-32 h-32 rounded-full bg-[#0AC18E]/20 flex items-center justify-center animate-pulse">
            <div className="w-24 h-24 rounded-full bg-[#0AC18E]/30 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-[#0AC18E] flex items-center justify-center shadow-xl shadow-[#0AC18E]/40">
                <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        <div>
          <h1 className="text-5xl font-black text-[#0AC18E] tracking-tight">PRO!</h1>
          <p className="text-lg text-zinc-500 dark:text-zinc-400 mt-2">All features unlocked. Thank you! 🎉</p>
        </div>
        <div className="w-full bg-white dark:bg-zinc-800 rounded-3xl p-5 border border-zinc-100 dark:border-zinc-700 text-left space-y-2">
          {PRO_FEATURES.map((f) => (
            <div key={f.label} className="flex items-center gap-3">
              <span className="text-xl">{f.icon}</span>
              <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">{f.label}</span>
              <span className="ml-auto text-[#0AC18E] text-lg font-bold">✓</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 pb-6">

      {/* ── Early adopter banner ── */}
      {isEarly ? (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-2xl px-4 py-3.5 flex items-center gap-3">
          <span className="text-2xl shrink-0">🚀</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-black text-amber-800 dark:text-amber-300">
              Early Adopter Price
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
              First {EARLY_LIMIT} merchants get Pro at ${priceUSD}. Price rises to $29 after that.
            </p>
          </div>
          {/* Spot counter */}
          <div className="shrink-0 text-right">
            {pricingLoading || spotsLeft === undefined ? (
              <div className="w-10 h-8 bg-amber-200 dark:bg-amber-800 rounded-lg animate-pulse" />
            ) : (
              <>
                <p className="text-xl font-black text-amber-700 dark:text-amber-300 leading-none">{spotsLeft}</p>
                <p className="text-[10px] font-semibold text-amber-500 dark:text-amber-500 uppercase tracking-wide">left</p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-zinc-100 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-2xl px-4 py-3 flex items-center gap-3">
          <span className="text-xl shrink-0">ℹ️</span>
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Early-adopter spots are gone. Regular price applies.
          </p>
        </div>
      )}

      {/* ── Spot progress bar (only during early period) ── */}
      {isEarly && spotsLeft !== undefined && (
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs font-semibold">
            <span className="text-zinc-500 dark:text-zinc-400">
              {EARLY_LIMIT - spotsLeft} of {EARLY_LIMIT} early spots claimed
            </span>
            <span className={cn(
              'font-black',
              spotsLeft <= 10 ? 'text-red-500' : 'text-amber-500'
            )}>
              {spotsLeft} remaining
            </span>
          </div>
          <div className="w-full bg-zinc-200 dark:bg-zinc-700 rounded-full h-2.5 overflow-hidden">
            <div
              className={cn(
                'h-2.5 rounded-full transition-all',
                spotsLeft <= 10 ? 'bg-red-500' : 'bg-amber-400'
              )}
              style={{ width: `${((EARLY_LIMIT - spotsLeft) / EARLY_LIMIT) * 100}%` }}
            />
          </div>
          {/* Verified on-chain note */}
          <p className="text-[10px] text-zinc-400 text-center">
            ✓ Live count verified on-chain via{' '}
            <a
              href={`https://bchexplorer.cash/address/${PRO_ADDRESS}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0AC18E] hover:underline"
            >
              bchexplorer.cash
            </a>
          </p>
        </div>
      )}

      {/* ── Hero price card ── */}
      <div className="bg-gradient-to-br from-[#0AC18E] to-[#09AF81] rounded-3xl p-6 text-center shadow-lg shadow-[#0AC18E]/25">
        <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-3">
          <span className="text-white font-black text-xl">BCH</span>
        </div>
        <h2 className="text-2xl font-black text-white mb-1">Upgrade to Pro</h2>
        <p className="text-white/75 text-sm">One-time payment · Yours forever</p>
        <div className="mt-4 flex items-center justify-center gap-3">
          <div className="bg-white/20 rounded-2xl px-6 py-3">
            {priceReady ? (
              <>
                <p className="text-3xl font-black text-white">{priceBCH} BCH</p>
                <p className="text-white/70 text-sm">${priceUSD} USD · {approxLocal}</p>
              </>
            ) : (
              <>
                <p className="text-white/60 text-sm animate-pulse">Fetching live price…</p>
                <p className="text-white/40 text-xs mt-0.5">${priceUSD} USD equivalent</p>
              </>
            )}
          </div>
          {isEarly && (
            <div className="flex flex-col items-center">
              <p className="text-white/50 text-xs line-through">${REGULAR_PRICE_USD}</p>
              <div className="bg-amber-400 text-amber-900 text-xs font-black px-2 py-0.5 rounded-full mt-0.5">
                SAVE $10
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Feature list ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 divide-y divide-zinc-100 dark:divide-zinc-700 overflow-hidden">
        {PRO_FEATURES.map((f) => (
          <div key={f.label} className="flex items-center gap-3 px-4 py-3.5">
            <span className="text-xl w-8 text-center shrink-0">{f.icon}</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{f.label}</p>
              <p className="text-xs text-zinc-400">{f.sub}</p>
            </div>
            <span className="text-[#0AC18E] font-bold text-lg shrink-0">✓</span>
          </div>
        ))}
      </div>

      {/* ── Pro Plus teaser ── */}
      <ProPlusTeaser />

      {/* ── Payment section ── */}
      {!showQR ? (
        <button
          onClick={handleShowQR}
          disabled={!priceReady}
          className="w-full py-5 rounded-2xl text-lg font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all disabled:opacity-60 disabled:cursor-wait"
        >
          {priceReady ? `Pay ${priceBCH} BCH to Unlock →` : 'Fetching live price…'}
        </button>
      ) : (
        <div className="flex flex-col items-center gap-4 bg-white dark:bg-zinc-800 rounded-3xl p-5 border border-zinc-100 dark:border-zinc-700">
          <p className="text-sm font-semibold text-zinc-700 dark:text-zinc-200 text-center">
            Scan with any BCH wallet
          </p>

          <div className="bg-white rounded-2xl p-3 shadow-sm border border-zinc-100">
            <QRCodeCanvas value={bchUri} size={qrSize} level="M" className="rounded-xl" />
          </div>

          <div className="text-center">
            <p className="text-2xl font-black text-zinc-900 dark:text-zinc-50">{lockedPriceBCH} BCH</p>
            <p className="text-sm text-zinc-400">${priceUSD} USD · {approxLocal} one-time</p>
          </div>

          <div className="w-full bg-zinc-50 dark:bg-zinc-900 rounded-xl p-3 border border-zinc-200 dark:border-zinc-700">
            <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider mb-1">Send to</p>
            <p className="text-xs font-mono text-zinc-600 dark:text-zinc-300 break-all leading-relaxed">
              {PRO_ADDRESS}
            </p>
          </div>

          <div className="flex items-center gap-2">
            {status === 'waiting' && (
              <>
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0AC18E] opacity-75" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#0AC18E]" />
                </span>
                <span className="text-sm text-zinc-500 dark:text-zinc-400">Waiting for payment...</span>
              </>
            )}
          </div>

          <button
            onClick={() => { setShowQR(false); setLockedPriceBCH(undefined); reset(); }}
            className="text-sm text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      <p className="text-xs text-center text-zinc-400 dark:text-zinc-600 px-4">
        Payment detected automatically. Unlock stored on this device. No account needed.
      </p>

      <button
        onClick={() => setView('checkout')}
        className={cn(
          'w-full py-4 rounded-2xl text-base font-semibold',
          'bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border border-zinc-200 dark:border-zinc-700'
        )}
      >
        Maybe Later
      </button>
    </div>
  );
}
